import React from 'react';

import type { ChatResponse } from '../api/chat.api';

const uuidRegex =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

export function stripIds(text: string): string {
  return text
    .replace(uuidRegex, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatPersonShortName(fullName: string): string {
  const clean = stripIds(fullName);
  const parts = clean.split(/\s+/).filter(Boolean);

  if (parts.length <= 1) {
    return clean;
  }

  return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
}

export function cleanOptionLabel(label: string, phase?: string): string {
  const cleaned = stripIds(label);
  const looksLikePersonStep = phase === 'buildGroups' || phase === 'confirmAll';
  const looksLikePerson =
    looksLikePersonStep && cleaned.split(/\s+/).filter(Boolean).length >= 2;

  return looksLikePerson ? formatPersonShortName(cleaned) : cleaned;
}

export function renderInlineMarkdown(text: string, foregroundColor: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  const boldRegex = /\*\*(.+?)\*\*/g;
  let cursor = 0;
  let match = boldRegex.exec(text);

  while (match) {
    const [whole, bold] = match;
    const start = match.index;

    if (start > cursor) {
      nodes.push(<React.Fragment key={`t-${cursor}`}>{text.slice(cursor, start)}</React.Fragment>);
    }

    nodes.push(
      <span key={`b-${start}`} style={{ fontWeight: 700, color: foregroundColor }}>
        {bold}
      </span>,
    );
    cursor = start + whole.length;
    match = boldRegex.exec(text);
  }

  if (cursor < text.length) {
    nodes.push(<React.Fragment key="t-end">{text.slice(cursor)}</React.Fragment>);
  }

  return nodes.length ? nodes : text;
}

export function markdownToTextLines(markdown: string): string[] {
  const normalized = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const paragraphs = normalized.split(/\n{2,}/);
  const lines: string[] = [];

  for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
    const paragraph = paragraphs[paragraphIndex].trim();

    if (paragraph) {
      lines.push(...paragraph.split('\n').map((line) => line.trimEnd()));

      if (paragraphIndex < paragraphs.length - 1) {
        lines.push('');
      }
    }
  }

  return lines.filter((line, index, allLines) => {
    return line.length > 0 || (index > 0 && allLines[index - 1].length > 0);
  });
}

export function isMessageSpacerLine(line: string): boolean {
  return line.trim().length === 0;
}

export function formatPhaseLabel(phase?: string): string | undefined {
  if (!phase) {
    return undefined;
  }

  const labels: Record<string, string> = {
    collectDate: 'Choose date',
    confirmSchedule: 'Confirm schedule',
    pickCourses: 'Pick courses',
    buildGroups: 'Add players',
    confirmAll: 'Review & confirm',
    completed: 'Submitted',
  };

  return labels[phase] ?? phase;
}

export function formatAvailabilityLabel(minutes: number): string {
  const hours = minutes / 60;

  if (hours === 1) {
    return '1 hour';
  }

  return `${hours} hours`;
}

export function buildCoursesPickMessage(
  courses: Array<{ label: string; priority: number }>,
): string {
  const list = courses.map((course) => `${course.label} (#${course.priority})`).join(', ');

  return `My course choices in priority order: ${list}`;
}

export function buildCoursePickMessage(label: string, priority: number): string {
  return buildCoursesPickMessage([{ label, priority }]);
}

export function buildAvailabilityPickMessage(minutes: number): string {
  return `My availability window is ${formatAvailabilityLabel(minutes)}`;
}

export function buildAvailabilityWindowPickMessage(label: string): string {
  const startLabel = label.split(/[–-]/)[0]?.trim() || label;

  return `My preferred tee time is ${startLabel} with a 3 hour availability window`;
}

export function buildTeeTimePickMessage(label: string): string {
  return `My preferred tee time is ${label}`;
}

export function formatDraftSummary(data: ChatResponse): string[] {
  const lines: string[] = [];
  const s = data.state;

  if (!s) {
    return lines;
  }

  const phase = formatPhaseLabel(s.phase);

  if (phase) {
    lines.push(`Step: ${phase}`);
  }

  if (s.displayDate) {
    lines.push(`Date: ${s.displayDate}`);
  } else if (s.resolvedDate || s.date) {
    lines.push(`Date: ${s.resolvedDate ?? s.date}`);
  }

  if (s.displayTimeEt) {
    lines.push(`Tee time: ${s.displayTimeEt}`);
  } else if (s.resolvedTimeEt || s.time) {
    lines.push(`Tee time: ${s.resolvedTimeEt ?? s.time} ET`);
  }

  const availability = s.availabilityMinutes ?? s.availability;

  if (availability != null) {
    lines.push(`Availability: ${formatAvailabilityLabel(availability)}`);
  }

  const selectedCourses = s.selectedCourseNames?.length
    ? s.selectedCourseNames.map((name, index) => ({ name, priority: index + 1 }))
    : s.courses
      ?.map((course) => ({
        name: course.name,
        priority: course.priority,
      }))
      .filter((course): course is { name: string; priority: number } => Boolean(course.name));

  if (selectedCourses?.length) {
    lines.push(
      `Courses: ${selectedCourses
        .map((course) => `#${course.priority} ${stripIds(String(course.name))}`)
        .join(', ')}`,
    );
  }

  if (s.selectedMemberNames?.length) {
    lines.push(
      `Players: ${s.selectedMemberNames.map((name) => formatPersonShortName(name)).join(', ')}`,
    );
  }

  if (s.selectedGuestNames?.length) {
    lines.push(`Guests: ${s.selectedGuestNames.join(', ')}`);
  }

  if (s.scheduleNotice) {
    lines.push(s.scheduleNotice);
  }

  return lines;
}

export function formatStateDetail(data: ChatResponse): string | undefined {
  const lines: string[] = [];
  const s = data.state;

  if (!s) {
    return undefined;
  }

  const courseLabels = s.options?.courses?.slice(0, 8).map((o) => cleanOptionLabel(o.label, s.phase));

  if (courseLabels?.length) {
    lines.push(`Course options:\n• ${courseLabels.join('\n• ')}`);
  }

  const teeTimeLabels = s.options?.teeTimePresets?.slice(0, 8).map((o) => o.label);

  if (teeTimeLabels?.length) {
    lines.push(`Tee time options (ET):\n• ${teeTimeLabels.join('\n• ')}`);
  }

  if (s.options?.availabilityWindowPresets?.length) {
    const availabilityWindowLabels = s.options.availabilityWindowPresets
      .map((option) => option.label)
      .join('\n• ');

    lines.push(`Availability window options:\n• ${availabilityWindowLabels}`);
  }

  if (s.options?.availabilityPresets?.length) {
    lines.push(
      `Availability options: ${s.options.availabilityPresets.map((m) => formatAvailabilityLabel(m)).join(', ')}`,
    );
  }

  return lines.length ? lines.join('\n') : undefined;
}

export function ticketSuccessLine(ticket: unknown): string | undefined {
  if (ticket && typeof ticket === 'object' && 'id' in ticket) {
    return `Request submitted (ticket id: ${(ticket as { id: string }).id}).`;
  }

  return 'Request submitted.';
}
